import { Box, CopyButton, Grid, ListContentIsland, RequisiteListItem } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import {
  useCardNumberQuery,
  useCardCvvQuery,
  useCardQuery,
  useCardCredentialsUri,
} from '../../../api/cards';
import { useMemo } from 'react';
import { formatAddress, formatCardExpirationDate, formatCardNumber } from '@razz-team/utils';
import { useBusinessQuery } from '../../../api/businesses';
import { Route } from '../../../routes/account.cards_.$cardId_.details';
import { MainLayout } from '../../Layout/MainLayout';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';
import { ApiCard } from '../../../openapi';

function RsaCardDetails({ cardId }: { cardId: string }) {
  const { t } = useTranslation();
  const cardQuery = useCardQuery({ cardId });
  const cardNumberQuery = useCardNumberQuery({ cardId, enabled: true });
  const cardCvvQuery = useCardCvvQuery({ cardId, enabled: true });

  const businessQuery = useBusinessQuery();

  const address = useMemo(
    () =>
      businessQuery.data?.business.address
        ? formatAddress(businessQuery.data.business.address)
        : null,
    [businessQuery.data?.business.address],
  );

  const copyAllText = useMemo(() => {
    const rows = [];
    if (cardQuery.data) {
      rows.push(`${t('Cardholder name')}: ${cardQuery.data.cardHolderName}`);
    }
    if (cardNumberQuery.data) {
      rows.push(`${t('Card number')}: ${formatCardNumber(cardNumberQuery.data.pan)}`);
    }
    if (cardQuery.data) {
      rows.push(`${t('Expiry date')}: ${formatCardExpirationDate(cardQuery.data.expirationDate)}`);
    }
    if (cardCvvQuery.data) {
      rows.push(`${t('CVV')}: ${cardCvvQuery.data.cvv}`);
    }
    if (address) {
      rows.push(`${t('Billing address')}: ${address}`);
    }
    return rows.join('\n');
  }, [cardQuery.data, cardNumberQuery.data, cardCvvQuery.data, address, t]);

  return (
    <ListContentIsland
      actions={
        copyAllText ? (
          <CopyButton value={copyAllText} variant="text">
            {t('Copy all')}
          </CopyButton>
        ) : undefined
      }
    >
      <RequisiteListItem
        title={t('Cardholder name')}
        value={cardQuery.data?.cardHolderName}
        loading={cardQuery.isPending}
        error={cardQuery.error}
      />
      <RequisiteListItem
        title={t('Card number')}
        value={cardNumberQuery.data ? formatCardNumber(cardNumberQuery.data.pan) : undefined}
        valueToCopy={cardNumberQuery.data?.pan}
        loading={cardNumberQuery.isPending}
        error={cardNumberQuery.error}
      />
      <RequisiteListItem
        title={t('Expiry date')}
        value={formatCardExpirationDate(cardQuery.data?.expirationDate)}
        loading={cardQuery.isPending}
        error={cardQuery.error}
      />
      <RequisiteListItem
        title={t('CVV')}
        value={cardCvvQuery.data?.cvv}
        loading={cardCvvQuery.isPending}
        error={cardCvvQuery.error}
      />
      <RequisiteListItem
        title={t('Billing address')}
        value={address}
        loading={businessQuery.isPending}
        error={businessQuery.error}
      />
    </ListContentIsland>
  );
}

function UriCardDetails({ card }: { card: ApiCard }) {
  const cardCredentialsQuery = useCardCredentialsUri({ cardId: card.id });

  return (
    <Box
      component="iframe"
      allow="clipboard-read; clipboard-write"
      src={cardCredentialsQuery.data?.uri}
      sx={{ width: '100%', height: '500px', border: 'none' }}
    />
  );
}

function CardIssuerDetails({ card }: { card: ApiCard }) {
  switch (card.cardIssuer) {
    case 'WALLESTER':
      return <RsaCardDetails cardId={card.id} />;
    case 'MARQETA':
    default:
      return <UriCardDetails card={card} />;
  }
}

export function CardDetailsPage() {
  const { t } = useTranslation();
  const { cardId } = Route.useParams();
  const cardQuery = useCardQuery({ cardId });

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Card details')}
          parent={{
            to: '/account/cards/$cardId',
            params: { cardId },
            title: cardQuery.data?.name,
            loading: cardQuery.isPending,
          }}
        />
      }
      compact
    >
      <Grid size={12}>{cardQuery.data ? <CardIssuerDetails card={cardQuery.data} /> : null}</Grid>
    </MainLayout>
  );
}
